<template>
  <el-select
    :model-value="modelValue"
    filterable
    remote
    reserve-keyword
    clearable
    :remote-method="scheduleSearch"
    :loading="loading"
    :placeholder="placeholder"
    :disabled="disabled"
    :class="selectClass"
    :no-data-text="noDataText"
    popper-class="live-id-select-dropdown"
    @update:model-value="onUpdate"
    @visible-change="onVisible"
  >
    <el-option
      v-for="item in options"
      :key="item.id"
      :label="labelOf(item)"
      :value="item.id!"
    >
      <div class="live-id-option">
        <span class="live-id-option__name">{{ item.title || '未命名直播' }}</span>
        <span class="live-id-option__id">编号 {{ item.id }}</span>
      </div>
    </el-option>
  </el-select>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount } from 'vue'
import { getRoom, getRoomPage, type LiveRoomVO } from '@/api/live/room'

const props = withDefaults(
  defineProps<{
    modelValue?: number | string | null
    placeholder?: string
    disabled?: boolean
    selectClass?: string
  }>(),
  {
    placeholder: '输入编号或标题搜索，点选下方列表',
    disabled: false,
    selectClass: '!w-240px'
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | undefined): void
  (e: 'change', value: number | undefined, room?: LiveRoomVO): void
}>()

const loading = ref(false)
const options = ref<LiveRoomVO[]>([])
const noDataText = ref('暂无数据，请输入编号或标题搜索')
let debounceTimer: ReturnType<typeof setTimeout> | undefined
let searchSeq = 0

const toNum = (v: number | string | null | undefined) => {
  if (v == null || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) && n > 0 ? n : undefined
}

const labelOf = (item: LiveRoomVO) => `#${item.id} ${item.title || '未命名直播'}`

const ensureOption = async (id?: number) => {
  if (!id) return
  if (options.value.some((item) => item.id === id)) return
  try {
    const one: any = await getRoom(id)
    if (one?.id) {
      options.value = [one, ...options.value.filter((x) => x.id !== one.id)]
      return
    }
  } catch {
    /* fallback */
  }
  options.value = [{ id, title: `直播 #${id}`, sourceType: 0 } as LiveRoomVO, ...options.value]
}

const search = async (keyword?: string) => {
  const seq = ++searchSeq
  loading.value = true
  noDataText.value = '暂无数据，请输入编号或标题搜索'
  try {
    const text = String(keyword || '').trim()
    const map = new Map<number, LiveRoomVO>()
    if (/^\d+$/.test(text)) {
      try {
        const one: any = await getRoom(Number(text))
        if (one?.id) map.set(one.id, one)
      } catch {
        /* continue */
      }
      if (map.size === 0) {
        const data: any = await getRoomPage({ pageNo: 1, pageSize: 20 })
        for (const row of data.list || []) {
          if (row?.id != null) map.set(row.id, row)
        }
        noDataText.value = `未找到直播 ${text}，可从下列直播间中选择或改用标题搜索`
      }
    } else {
      const params: Record<string, any> = { pageNo: 1, pageSize: 20 }
      if (text) params.title = text
      const data: any = await getRoomPage(params)
      for (const row of data.list || []) {
        if (row?.id != null) map.set(row.id, row)
      }
    }
    if (seq !== searchSeq) return
    options.value = Array.from(map.values())
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

const scheduleSearch = (keyword?: string) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    void search(keyword)
  }, 200)
}

const onUpdate = (val: number | undefined) => {
  const id = toNum(val)
  emit('update:modelValue', id)
  const room = id == null ? undefined : options.value.find((item) => item.id === id)
  emit('change', id, room)
}

const onVisible = async (open: boolean) => {
  if (open) await search('')
}

watch(
  () => props.modelValue,
  async (val) => {
    const id = toNum(val)
    if (id) await ensureOption(id)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
})

defineExpose({ search, ensureOption })
</script>

<style scoped>
.live-id-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
}
.live-id-option__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-id-option__id {
  flex-shrink: 0;
  color: var(--el-text-color-secondary);
  font-size: 12px;
}
</style>
